import { defer, Subject, startWith, Observable } from 'rxjs';

import { Dialog, DialogConfig, CdkDialogContainer } from '@angular/cdk/dialog';
import { Overlay, ComponentType, ScrollStrategy, OverlayContainer } from '@angular/cdk/overlay';
import {
  Type,
  inject,
  Inject,
  Injector,
  Optional,
  SkipSelf,
  OnDestroy,
  Injectable,
  ComponentRef,
  InjectionToken,
} from '@angular/core';

import { AppDialogRef, AppDialogConfig, BaseDialogComponent } from '@/app/shared/components/ui/dialog';

export const APP_DIALOG_DATA = new InjectionToken<any>('AppDialogData');

export const APP_DIALOG_DEFAULT_OPTIONS = new InjectionToken<AppDialogConfig>('app-dialog-default-options');

export const APP_DIALOG_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>('app-dialog-scroll-strategy', {
  providedIn: 'root',
  factory: () => {
    const overlay = inject(Overlay);
    return () => overlay.scrollStrategies.block();
  },
});

export function APP_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

export const APP_DIALOG_SCROLL_STRATEGY_PROVIDER = {
  provide: APP_DIALOG_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: APP_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
};

let uniqueId = 0;

@Injectable({ providedIn: 'root' })
export class AppDialog implements OnDestroy {
  private readonly _openDialogsAtThisLevel: AppDialogRef<any>[] = [];

  private readonly _afterAllClosedAtThisLevel = new Subject<void>();

  private readonly _afterOpenedAtThisLevel = new Subject<AppDialogRef<any>>();

  protected _dialog: Dialog;

  protected dialogConfigClass = AppDialogConfig;

  private readonly _dialogContainerType: Type<CdkDialogContainer>;

  private readonly _dialogRefConstructor: Type<AppDialogRef<any>>;

  private readonly _dialogDataToken: InjectionToken<any>;

  get openDialogs(): AppDialogRef<any>[] {
    return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
  }

  get afterOpened(): Subject<AppDialogRef<any>> {
    return this._parentDialog ? this._parentDialog.afterOpened : this._afterOpenedAtThisLevel;
  }

  private _getAfterAllClosed(): Subject<void> {
    const parent = this._parentDialog;
    return parent ? parent._getAfterAllClosed() : this._afterAllClosedAtThisLevel;
  }

  readonly afterAllClosed: Observable<void> = defer(() =>
    this.openDialogs.length ? this._getAfterAllClosed() : this._getAfterAllClosed().pipe(startWith(undefined)),
  ) as Observable<any>;

  constructor(
    private _overlay: Overlay,
    injector: Injector,
    @Optional() @Inject(APP_DIALOG_DEFAULT_OPTIONS) private _defaultOptions: AppDialogConfig,
    @Inject(APP_DIALOG_SCROLL_STRATEGY) private _scrollStrategy: any,
    @Optional() @SkipSelf() private _parentDialog: AppDialog,
    _overlayContainer: OverlayContainer,
  ) {
    this._dialog = injector.get(Dialog);

    this._dialogRefConstructor = AppDialogRef;
    this._dialogContainerType = CdkDialogContainer;
    this._dialogDataToken = APP_DIALOG_DATA;
  }

  open<DialogData, DialogResult>(
    component: ComponentType<BaseDialogComponent<DialogData, DialogResult>>,
    config?: AppDialogConfig<DialogData>,
  ): AppDialogRef<BaseDialogComponent<DialogData, DialogResult>, DialogResult> {
    let dialogRef: AppDialogRef<BaseDialogComponent<DialogData, DialogResult>, DialogResult>;

    config = { ...(this._defaultOptions || new AppDialogConfig()), ...config };
    config.id = config.id || `dialog-${uniqueId++}`;
    config.scrollStrategy = config.scrollStrategy || this._scrollStrategy();

    const cdkRef = this._dialog.open<DialogResult, DialogData, BaseDialogComponent<DialogData, DialogResult>>(
      component,
      {
        ...config,
        positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
        disableClose: true,
        closeOnDestroy: false,
        closeOnOverlayDetachments: false,
        container: {
          type: this._dialogContainerType,
          providers: () => [
            { provide: this.dialogConfigClass, useValue: config },
            { provide: DialogConfig, useValue: config },
          ],
        },
        templateContext: () => ({ dialogRef }),
        providers: (ref, cdkConfig) => {
          dialogRef = new this._dialogRefConstructor(ref, config);
          dialogRef.updatePosition(config?.position);
          return [
            { provide: this._dialogDataToken, useValue: cdkConfig.data },
            { provide: this._dialogRefConstructor, useValue: dialogRef },
          ];
        },
      },
    );

    (dialogRef! as { componentRef: ComponentRef<BaseDialogComponent<DialogData, DialogResult>> }).componentRef =
      cdkRef.componentRef!;
    dialogRef!.componentInstance = cdkRef.componentInstance!;

    this.openDialogs.push(dialogRef!);
    this.afterOpened.next(dialogRef!);

    return dialogRef!;
  }

  closeAll(): void {
    this._closeDialogs(this.openDialogs);
  }

  getDialogById(id: string): AppDialogRef<any> | undefined {
    return this.openDialogs.find((dialog) => dialog.id === id);
  }

  ngOnDestroy() {
    this._closeDialogs(this._openDialogsAtThisLevel);
    this._afterAllClosedAtThisLevel.complete();
    this._afterOpenedAtThisLevel.complete();
  }

  private _closeDialogs(dialogs: AppDialogRef<any>[]) {
    let i = dialogs.length;

    while (i--) {
      dialogs[i].close();
    }
  }
}
